import { IconButton, Tooltip, Typography } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React, { useState } from 'react'

import { TicketReporter } from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'

interface ReporterIdentityProps {
  reporter?: TicketReporter | null
}

/**
 * How a reporter's type reads to a person.
 *
 * All four the server can send, not the two this screen has happened to
 * receive: an unhandled value fell through to the raw enum, and REPORTER_TYPE_
 * SERVICE is exactly the case an operator would be most puzzled by.
 */
const REPORTER_TYPE_LABELS: Record<string, string> = {
  REPORTER_TYPE_USER: 'User',
  REPORTER_TYPE_EXTERNAL: 'External contact',
  REPORTER_TYPE_SERVICE: 'Service',
  REPORTER_TYPE_UNKNOWN: 'Unknown'
}

export function reporterTypeLabel(type?: string | null): string {
  if (!type) return getTranslate('Unknown')
  // The raw value when it is one this build does not know. A server that adds
  // a fifth type should read oddly here, not silently as "Unknown" — which
  // would assert something false about a reporter that does have a type.
  return REPORTER_TYPE_LABELS[type]
    ? getTranslate(REPORTER_TYPE_LABELS[type])
    : type
}

/**
 * Which organization a reporter belongs to, said rather than encoded.
 *
 * This rendered as `accommodation:HOTEL-A` — a type token, a colon, and a code
 * — which is a database key wearing a label's clothes. The type is a known
 * enum, so it can be spoken; the code stays verbatim because it is what the
 * customer sees on their own tickets and is the string they would quote.
 *
 * The code keeps LTR isolation and the type does not: one is a Latin machine
 * identifier, the other is a translated noun that reads right-to-left under
 * /fa.
 */
const ENTITY_TYPE_LABELS: Record<string, string> = {
  accommodation: 'Accommodation',
  channel: 'Channel',
  provider: 'Provider',
  tenant: 'Tenant',
  adotel: 'Adotel'
}

export function entityLabel(reporter: TicketReporter): string {
  const type = (reporter.entityType || '').toLowerCase()
  // The raw value when this build does not know the type, for the same reason
  // reporterTypeLabel does it: a server that adds a sixth entity type should
  // read oddly here rather than silently as something it is not.
  const spoken = ENTITY_TYPE_LABELS[type]
    ? getTranslate(ENTITY_TYPE_LABELS[type])
    : reporter.entityType || ''
  // The NAME when the directory could answer, the code when it could not.
  //
  // "Accommodation TST-HOTEL-001" named the row without naming the hotel, which
  // is the complaint this whole change answers. The code is not shown alongside
  // the name: an operator who recognises "Test Hotel One" gains nothing from
  // the key beside it, and the pair is what made the overlay unreadable in the
  // first place. It remains the fallback, so a directory outage degrades to the
  // previous screen rather than to a blank.
  //
  // Each half is still omitted independently. The first version interpolated
  // the code unconditionally and leaned on .trim(), which only ever protected
  // the LEADING space — an absent code stringified to the literal text
  // "Accommodation undefined" on screen. The sole call site guards both fields
  // together, so it was unreachable; the function is exported, so the next
  // caller would have found it. Reachability by luck of one call site is not a
  // property worth keeping.
  return [spoken, reporter.entityName || reporter.entityCode]
    .filter(Boolean)
    .join(' ')
}

/**
 * The reporter's name, with the rest of their identity behind an info button.
 *
 * This line read `Reza Ahmadi (REPORTER_TYPE_USER)` — a protobuf enum printed
 * verbatim beside a person's name. The parenthetical was the only place the
 * type appeared, so it could not simply be dropped; it moves into the overlay
 * along with the identifiers that had nowhere to go at all.
 *
 * An IconButton rather than a span with a tooltip: MUI's Tooltip opens on
 * FOCUS as well as hover, so a focusable trigger makes the overlay reachable
 * by keyboard for free. A tooltip hung on a non-focusable element is
 * mouse-only, which is the same defect the date picker had.
 */
export default function ReporterIdentity({ reporter }: ReporterIdentityProps) {
  // Above the early return, not below it. A hook after a conditional return
  // changes React's hook order the first time `reporter` goes from absent to
  // present, and eslint did not object — react-hooks/rules-of-hooks is not
  // enabled in this project, so the usual backstop is not there.
  const [open, setOpen] = useState(false)

  if (!reporter) {
    return (
      <Typography variant="body2" color="textSecondary">
        {getTranslate('Unknown')}
      </Typography>
    )
  }

  const name =
    reporter.displayName || reporter.userId || getTranslate('Unknown')

  const details: Array<[string, string]> = [
    [getTranslate('Type'), reporterTypeLabel(reporter.type)]
  ]
  // Only what this reporter actually has. A row reading "User ID: —" for an
  // external contact states an absence as though it were a missing value.
  //
  // ORDER CHANGED, and the reason is the reported complaint: this overlay led
  // with two opaque identifiers — the raw user id and `accommodation:HOTEL-A` —
  // which is the least useful pair of facts available about a colleague. The
  // organization now leads, written the way it is spoken, and the id follows as
  // the reference it is.
  if (reporter.entityType && reporter.entityCode) {
    details.push([getTranslate('Organization'), entityLabel(reporter)])
  }
  if (reporter.serviceCode) {
    details.push([getTranslate('Service'), reporter.serviceCode])
  }
  // The raw user id is NOT shown. It was, argued for as "the only stable handle
  // for correlating a person across services" — which is true and is not the
  // operator's problem. Ids here are opaque encoded tokens
  // (`usr_O8ZV6oGcLQeiZCOIscDQAtDyALRPq_...`), so the row occupied four lines of
  // a tooltip to tell a support agent nothing they could act on. Reported as
  // useless twice before it came out.
  //
  // Nothing is lost for the outage case: when the directory cannot resolve a
  // name, `displayNameFor` already falls back to the id in the HEADING, so it is
  // still on screen exactly when it is the only thing there is.

  return (
    <Typography variant="body2" color="textSecondary" component="div">
      <bdi>{name}</bdi>
      <Tooltip
        /* Controlled, because the uncontrolled Tooltip has no touch story: it
           opens on a 700ms long-press nothing advertises and closes itself on
           a timer. A tap did nothing at all. Click now toggles it, which is
           the same gesture on every input device. */
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        /* Without this the tooltip unmounts the moment the pointer moves onto
           it — and these are identifiers a person wants to select and copy,
           not a hint they glance at. WCAG 1.4.13 "Hoverable". */
        interactive
        title={
          <div>
            {details.map(([label, value]) => (
              <div key={label}>
                {label}: <bdi dir="ltr">{value}</bdi>
              </div>
            ))}
          </div>
        }>
        {/* The accessible name says what the button reveals, not "info" —
            several of these could end up on one screen, and "info" would name
            them all identically.

            fontSize="small" rather than "inherit": inheriting from body2 gave
            a ~22px target, under both the 24px WCAG 2.2 minimum and the 44pt
            platform guidance for anything reachable by thumb. */}
        <IconButton
          size="small"
          aria-label={getTranslate('Reporter details')}
          aria-expanded={open}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          style={{ marginInlineStart: 4 }}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Typography>
  )
}
